import { IColumn, IconButton, Stack, Text, TooltipHost } from '@fluentui/react';
import { GenericAttachForm } from 'common/components/ActionMenu/Attach/AttachFormModal';
import { DocumentDataCallout } from 'common/components/AttachDocumentModal/DocumentDataCallout';
import { DocumentViewModalState } from 'common/components/DocumentList';
import { TABLE_ROWS } from 'common/constants';
import {
  AttachableTravelerDocuments,
  AttachableTravelerDocuments_attachableTravelerDocuments_nodes,
} from 'common/graphql/__generated__/AttachableTravelerDocuments';
import { AttachTravelerDocumentVariables } from 'common/graphql/__generated__/AttachTravelerDocument';
import { TravelerAvailableDocumentTypes } from 'common/graphql/__generated__/TravelerAvailableDocumentTypes';
import { toOrderByVariable } from 'common/utils/actionMenuUtils';
import {
  dateConvertions,
  dateFormat,
  getGlobalDateFormat,
} from 'common/utils/dateFormats';
import React, { useCallback } from 'react';
import { Traveler_traveler } from 'settings/account/Travelers/view/__generated__/Traveler';
import { _columns } from './columns.data';
import { useStyles } from './index.styles';

import ATTACHABLE_TRAVELER_DOCUMENTS from 'common/graphql/AttachableTravelerDocuments.graphql';
import ATTACH_TRAVELER_DOCUMENTS from 'common/graphql/AttachTravelerDocument.graphql';
import TRAVELER from '../../../../Traveler.graphql';

type AttachableDataType =
  AttachableTravelerDocuments_attachableTravelerDocuments_nodes;
interface AttachFormModalProps {
  availableDocumentTypes: TravelerAvailableDocumentTypes | undefined;
  traveler: Traveler_traveler;
  isDirty: boolean;
}

export const AttachFormModal: React.FC<AttachFormModalProps> = ({
  availableDocumentTypes,
  traveler,
}) => {
  const styles = useStyles();

  const getDocumentTypeOptions = useCallback(
    (data: typeof availableDocumentTypes | undefined) => {
      return (
        data?.travelerAvailableDocumentTypes?.nodes.map((doctype) => ({
          key: doctype.id,
          text: doctype.documentType || '',
          title:
            !traveler?.isDraft && doctype.isAccountingDocument
              ? 'Upload prohibited during/after the approval process'
              : undefined,
          disabled:
            !traveler?.isDraft && doctype.isAccountingDocument
              ? true
              : undefined,
        })) || []
      );
    },
    [traveler]
  );

  const canSelectItem = useCallback(
    (item: AttachableDataType) =>
      traveler?.isDraft ? true : !item._isAccountingDocument!,
    [traveler]
  );

  const transformData = useCallback(
    (data: AttachableTravelerDocuments | undefined) => {
      return data?.attachableTravelerDocuments?.nodes || [];
    },
    []
  );

  const getMutationInput = useCallback(
    (selectedItems: AttachableDataType[], entityId: string) => {
      const entityDocIds = selectedItems.map((item) => item.id);
      return {
        input: {
          travelerId: entityId,
          entityDocumentId: entityDocIds,
        },
      } as AttachTravelerDocumentVariables;
    },
    []
  );

  const onRenderItemColumn = useCallback(
    (
      item?: AttachableDataType,
      _index?: number,
      column?: IColumn,
      openDocumentViewModal?: (state: DocumentViewModalState) => void
    ) => {
      if (!item || !column) return undefined;

      const disableLogic = traveler?.isDraft
        ? false
        : item._isAccountingDocument;

      const fieldContent = item[
        column.fieldName as keyof AttachableDataType
      ] as string | null;

      switch (column.key) {
        case 'fileReference':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <DocumentDataCallout disabled={disableLogic!} item={item} />
            </Stack>
          );
        case 'documentType':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item.documentTypes?.documentType}
              </Text>
            </Stack>
          );
        case 'view':
          const viewDocumentVisible =
            item._isProtected! || item._fileViewer !== 'browser';
          return (
            <Stack
              className={styles.columnHeight}
              tokens={{ childrenGap: 10 }}
              horizontal
              verticalAlign="center"
            >
              <TooltipHost content="View" id="tooltipId">
                <IconButton
                  disabled={viewDocumentVisible}
                  iconProps={{ iconName: 'View' }}
                  onClick={() =>
                    openDocumentViewModal?.({
                      isOpen: true,
                      title: item.fileReference,
                      entityDocumentId: item.id,
                      _fileType: item._fileType!,
                    })
                  }
                />
              </TooltipHost>
            </Stack>
          );
        case 'indexAmount':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <Text className={styles.contentColumnAlignRight}>
                {fieldContent}
              </Text>
            </Stack>
          );
        case 'isoCode':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item.currency?.isoCode}
              </Text>
            </Stack>
          );
        case '_uploadDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item._uploadDate ? getGlobalDateFormat(item._uploadDate) : ''}
              </Text>
            </Stack>
          );
        case 'indexTransactionDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              {item.indexTransactionDate && (
                <Text className={disableLogic ? styles.disabledText : ''}>
                  {dateFormat(dateConvertions(item.indexTransactionDate))}
                </Text>
              )}
            </Stack>
          );
        default:
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {fieldContent}
              </Text>
            </Stack>
          );
      }
    },
    [styles, traveler]
  );

  return (
    <GenericAttachForm
      entity={{
        id: traveler.id!,
        isDraft: traveler.isDraft,
      }}
      availableDocumentTypes={availableDocumentTypes}
      attachableDocumentsQuery={ATTACHABLE_TRAVELER_DOCUMENTS}
      attachDocumentsMutation={ATTACH_TRAVELER_DOCUMENTS}
      detailsQuery={TRAVELER}
      columns={_columns}
      modalWidth={1350}
      getDocumentTypeOptions={getDocumentTypeOptions}
      transformData={transformData}
      canSelectItem={canSelectItem}
      getMutationInput={getMutationInput}
      onRenderItemColumn={onRenderItemColumn}
      initialQueryVariables={{
        travelerId: traveler.id!,
        first: TABLE_ROWS,
      }}
      toOrderByVariable={toOrderByVariable}
    />
  );
};
